<template>
  <w-setting-sliding-popup ref="slidingPopup" :title="popupTitle" width="500px" @close="close()">
    <div slot="content">
      <el-form :model="formDatas" ref="subOidForm" label-position="top" :rules="formRules">
        <div class="title-bar title-h4">기본 정보</div>
        <el-row>
          <el-form-item label="OID" prop="snmpOid">
            <span v-if="title === 'Sub OID 수정'">{{formDatas.snmpOid}}</span>
            <el-input v-else v-model="formDatas.snmpOid" />
          </el-form-item>
        </el-row>
        <el-row>
          <el-form-item label="OID명" prop="oidName">
            <el-input v-model="formDatas.oidName" />
          </el-form-item>
        </el-row>
        <el-row>
          <el-form-item label="설명">
            <el-input type="textarea" :rows="3" v-model="formDatas.oidDescr" resize="none" />
          </el-form-item>
        </el-row>
        <el-row class="mr-b25">
          <el-col>
            <el-form-item label="메시지 사용여부">
              <el-switch v-model="formDatas.messageFlag" active-value="Y" inactive-value="N" />
            </el-form-item>
          </el-col>
        </el-row>
        <div class="title-bar title-h4">메시지</div>
        <el-row>
          <el-col :span="24">
            <el-row type="flex" justify="space-between">
              <el-col class="mr-r4" :span="10">
                <el-form-item label="값">
                  <el-input v-model="message.oidValue" :disabled="formDatas.messageFlag === 'N'"/>
                </el-form-item>
              </el-col>
              <el-col :span="14">
                <el-form-item label="메시지">
                  <el-row type="flex" justify="space-between">
                    <el-col class="mr-r4" :span="21">
                      <el-input v-model="message.valueText" :disabled="formDatas.messageFlag === 'N'"/>
                    </el-col>
                    <el-col :span="3" align="right">
                      <el-button size="mini" icon="fas fa-plus" :disabled="formDatas.messageFlag === 'N'" @click="addMessage"/>
                    </el-col>
                  </el-row>
                </el-form-item>
              </el-col>
            </el-row>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="24">
            <el-table-wrapper :data="messages" :columns="columns" :pagination="false" highlight-current-row>
              <template slot-scope="scope" slot="delete-row-slot">
                <span class="c-pointer" @click="onDeleteRow(scope.row)"><i class="fal fa-times" /></span>
              </template>
              <template slot-scope="scope" slot="value-text-slot">
                <el-input v-model="scope.row.valueText" @blur="onUpdateMessage(scope.row)" />
              </template>
            </el-table-wrapper>
          </el-col>
        </el-row>
      </el-form>
    </div>
    <div slot="footer">
      <el-button size="large" type="solid" @click="onSaveButton">저장</el-button>
      <el-button size="large" @click="clickClose">취소</el-button>
    </div>
  </w-setting-sliding-popup>
</template>

<script>
import remove from 'lodash/remove';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';
import { stringify } from 'querystring';

import msgUtils from '@/utils/message';
import integratedTrapSettingAPI from '@/api/settings/log/integrated-trap-settings';

export default {
  name: 'WSuboidSettingPopup',
  props: {
    title: {
      type: String,
      required: true,
    },
    oidData: {
      type: Object,
    },
    suboidData: {
      type: Object,
    },
    suboidTableDatas: {
      type: Array,
    },
  },
  data() {
    const snmpOidRule = (rule, value, callback) => {
      if (!value) {
        return callback(new Error('필수 정보입니다.'));
      }
      // TODO Oid Validation 검사
      if (!/^([1-9][0-9]{0,3}|0)(\.([1-9][0-9]{0,4}|0)){5,250}$/.test(value) && this.title === 'Sub OID 추가') {
        return callback(new Error('잘못된 형식입니다.'));
      }
      const checkDupl = findIndex(this.suboidTableDatas, { snmpOid: this.formDatas.snmpOid });
      if (checkDupl !== -1 && this.title === 'Sub OID 추가') {
        return callback(new Error('이미 존재하는 OID입니다.'));
      }
      return callback();
    };
    const oidNameRule = (rule, value, callback) => {
      if (value && value.length > 128) {
        return callback(new Error('128자 이내로 입력해주세요.'));
      }
      return callback();
    };
    const formRules = {
      snmpOid: [
        { required: true, validator: snmpOidRule, trigger: 'blur' },
      ],
      oidName: [
        { validator: oidNameRule, trigger: 'blur' },
      ],
    };
    const columns = [
      {
        prop: 'oidValue',
        label: '값',
        width: '150px',
        headerAlign: 'center',
      },
      {
        prop: 'valueText',
        label: '메시지',
        headerAlign: 'center',
        scopedSlot: 'value-text-slot',
      },
      {
        scopedSlot: 'delete-row-slot',
        width: '30px',
      },
    ];

    return {
      formRules,
      formDatas: {},
      oidDataClone: {},
      messages: [],
      originMessages: [],
      message: {
        oidValue: '',
        valueText: '',
      },
      columns,
      addList: [],
      deleteList: [],
      updateList: [],
    };
  },
  watch: {
    oidData: {
      immediate: true,
      handler() {
        if (!isEmpty(this.oidData)) {
          this.oidDataClone = cloneDeep(this.oidData);
        }
      },
    },
    suboidData: {
      immediate: true,
      handler() {
        if (!isEmpty(this.suboidData)) {
          this.formDatas = cloneDeep(this.suboidData);
          this.loadMessages(this.formDatas.snmpOid);
        }
      },
    },
  },
  computed: {
    popupTitle() {
      return `${this.title} [${this.oidDataClone.snmptrapOid}]`;
    },
  },
  methods: {
    loadMessages(snmpOid) {
      const params = {
        params: {
          mibOid: snmpOid,
        },
      };
      integratedTrapSettingAPI.getMibOidValue(params).then((response) => {
        this.messages = cloneDeep(response.data.data);
        this.originMessages = cloneDeep(response.data.data);
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '메시지', false));
        throw new Error(e);
      });
    },
    addMessage() {
      const checkDupl = this.messages.filter((item) => item.oidValue === this.message.oidValue);
      if (isEmpty(this.message.oidValue) || isEmpty(this.message.valueText)) {
        this.$message(msgUtils.getCustomMsgData('메시지와 값을 모두 입력해주세요.'));
        return;
      }
      if (!isEmpty(checkDupl)) {
        this.$message(msgUtils.getCustomMsgData('중복된 메시지입니다.'));
        return;
      }
      if (this.message.oidValue && this.message.oidValue.length > 64) {
        this.$message(msgUtils.getCustomMsgData('값은 64자 이내로 입력해주세요.'));
        return;
      }
      if (this.message.valueText && this.message.valueText.length > 128) {
        this.$message(msgUtils.getCustomMsgData('메시지는 128자 이내로 입력해주세요.'));
        return;
      }
      const message = cloneDeep(this.message);
      this.addList.push(message);
      this.messages.push(message);
      this.message = {
        oidValue: '',
        valueText: '',
      };
    },
    onDeleteRow(row) {
      this.deleteList.push(row);
      // message리스트에서 삭제된 값을 제거한다.
      this.messages = remove(this.messages, (item) => item.oidValue !== row.oidValue);
      // 만약 업데이트 리스트에 해당 항목이 들어가있을 경우 updateList에서 값을 제거한다.
      this.updateList = remove(this.updateList, (item) => item.oidValue === row.oidValue);
    },
    onUpdateMessage(row) {
      const message = this.originMessages.filter((item) => item.oidValue === row.oidValue);
      if (message.valueText !== row.valueText) {
        this.updateList.push(row);
      } else {
        this.updateList = remove(this.updateList, (item) => item.oidValue !== row.oidValue);
      }
    },
    onSaveButton() {
      this.$refs.subOidForm.validate().then((success) => {
        let snmpOid = this.formDatas.snmpOid;
        if (snmpOid.indexOf('.') === 0) {
          snmpOid = snmpOid.substring(1, snmpOid.length);
          this.formDatas.snmpOid = snmpOid;
        }
        if (success) {
          const params = stringify({
            oidName: this.formDatas.oidName,
            oidDescr: this.formDatas.oidDescr,
            snmpOid: this.formDatas.snmpOid,
            messageFlag: this.formDatas.messageFlag,
            snmptrapOid: this.oidDataClone.snmptrapOid,
            snmptrapVersion: this.oidDataClone.snmptrapVersion,
          });
          if (this.title === 'Sub OID 추가') {
            const messages = this.messages;
            integratedTrapSettingAPI.insertSubOid(params, messages).then((response) => {
              if (response.data.success) {
                if (response.data.data === 'Duplicate') {
                  this.$message(msgUtils.getCustomMsgData('중복된 Sub OID입니다.'));
                  return;
                } else if (response.data.data === 'Error') {
                  this.$message(msgUtils.getResultMsgData(msgUtils.action.ADD, 'Sub OID', response.data.success));
                }
                this.$message(msgUtils.getResultMsgData(msgUtils.action.ADD, 'Sub OID', response.data.success));
                this.clickClose();
                this.$emit('update-suboid-inform');
              } else {
                this.$message(msgUtils.getResultMsgData(msgUtils.action.ADD, 'Sub OID', response.data.success));
              }
            }).catch((e) => {
              this.$message(msgUtils.getResultMsgData(msgUtils.action.ADD, 'Sub OID', false));
              throw new Error(e);
            });
          } else {
            const messages = {
              addList: this.addList,
              deleteList: this.deleteList,
              updateList: this.updateList,
            };
            integratedTrapSettingAPI.updateSubOid(params, messages).then((response) => {
              if (response.data.success) {
                this.$message(msgUtils.getResultMsgData(msgUtils.action.MODIFY, 'Sub OID', response.data.success));
                this.clickClose();
                this.$emit('update-suboid-inform');
              } else {
                this.$message(msgUtils.getResultMsgData(msgUtils.action.MODIFY, 'Sub OID', response.data.success));
              }
            }).catch((e) => {
              this.$message(msgUtils.getResultMsgData(msgUtils.action.MODIFY, 'Sub OID', false));
              throw new Error(e);
            });
          }
        }
      });
    },
    close() {
      this.formDatas = {
        messageFlag: 'N',
      };
      this.$emit('delete-suboid-inform');
      this.addList = [];
      this.deleteList = [];
      this.updateList = [];
      this.message = {
        oidValue: '',
        valueText: '',
      };
      this.messages = [];
      // validation 초기화
      const clear = ['snmpOid', 'oidName'];
      this.$refs.subOidForm.clearValidate(clear);
    },
    clickClose() {
      this.$refs.slidingPopup.close();
      this.close();
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/settings/log-analysis/Collect/SnmpTrap/popup/SuboidSettingPopup.vue